import React, { useEffect, useState } from 'react';

import constants from 'constants/constants';
import { isEmpty } from 'lodash';
import moment from 'moment-timezone';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { isStringHasRealContent, sortAlphabeticalArray } from 'utilitys/helpers/general';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import { useHashValue } from 'hooks/useHashValue';

import { useCommentsStore } from 'store/comments.store';
import { useCommentsEditorStatus } from 'store/commentsEditorStatus.store';
import { useProjectTreeStore } from 'store/projectContract.store';
import { useVersionStore } from 'store/version.store';

import { useMutationCreateCard } from 'api/mutations/createCard.mutation';
import { useMutationCreateEditComment } from 'api/mutations/createEditComment.mutation';
import { useMutationGetActivityOptions } from 'api/mutations/getActivityOptions.mutation';
import { useMutationGetWBSOptions } from 'api/mutations/getWBSOptions.mutation';
import { useQueryGetBoardInitLite } from 'api/queries/getBoardInitLite.query';

import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { ConfirmChangesModal } from 'components/common/confirmChangesModal/confirmChangesModal';

import {
    IActiveTab,
    TabsSection,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/Common/TabsSection/TabsSection';
import { IFields } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/CreateCardModal/CreateCardModal.types';
import { buildCreateCardPayload } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/CreateCardModal/CreateCardModal.utils';
import { useMainCardProvider } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/MainCard.provider';
import { mapper } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/MainCard.utils';
import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/UserActionCardModal/UserActionCardModal.module.scss';
import { tabsArrData } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/UserActionCardModal/UserActionCardModal.utils';
import { CardContent } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/UserActionCardModal/components/CardContent/CardContent';
import { CardHeader } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/UserActionCardModal/components/CardHeader/CardHeader';
import { SideMenu } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/UserActionCardModal/components/SideMenu/SideMenu';
import { UserGeneratedCardStatusSelect } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/UserActionCardModal/components/UserGeneratedCardStatusSelect/UserGeneratedCardStatusSelect';
import {
    assigneeMapper,
    versionMapper,
    wbsMapper,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/helper';
import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';

interface IUserGeneratedCard {
    saveCardCallback: (value?: number) => void;
    deleteCardCallback: () => void;
    queryKeys?: string[];
}

export const UserActionCardModal = ({ saveCardCallback, deleteCardCallback, queryKeys = [] }: IUserGeneratedCard) => {
    const { showModal, setShowModal, api } = useMainCardProvider();
    const commentData = useCommentsStore((store) => store.commentData);
    const { resetCommentData, setCommentData, setCommentId } = useCommentsStore();
    const { resetCommentsEditorStatus } = useCommentsEditorStatus();

    const queryClient = useQueryClient();

    const hashValue = useHashValue();
    const activeTabByHash = hashValue ? (hashValue as IActiveTab) : 'cardDetails';

    const { pathname, search } = useLocation();
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<IActiveTab>(activeTabByHash);

    useEffect(() => {
        hashValue && setActiveTab(hashValue as IActiveTab);
    }, [hashValue]);

    const history = useHistory();
    const { project } = useProjectTreeStore();
    const { data: boardInit, isLoading: boardInitLoading } = useQueryGetBoardInitLite({ projectId: project?.id });
    const { mutate: mutateWBSOptions, isLoading: wbsOptionsOLoading } = useMutationGetWBSOptions();
    const { mutate: mutateActivityOptions, isLoading: activityOptionsLoading } = useMutationGetActivityOptions();
    const { mutate: mutateCreateCard, isLoading: createCardLoading } = useMutationCreateCard();
    const { mutate: createEditCommentMutate, isLoading: createEditCommentLoading } = useMutationCreateEditComment();
    const currentVersionFromStore = useVersionStore((state) => state.version);

    const { handleSnackBar } = useCustomSnackBar();

    const isLoading =
        boardInitLoading ||
        wbsOptionsOLoading ||
        activityOptionsLoading ||
        createCardLoading ||
        createEditCommentLoading;

    const {
        control,
        handleSubmit,
        formState: { errors, dirtyFields },
        setValue,
        getValues,
        watch,
        reset,
    } = useForm<IFields>();

    // reset comments on first load - because 'handleModalClose' execute only on Cancel button click
    useEffect(() => {
        resetCommentsEditorStatus();
        resetCommentData();
    }, []);

    // ===============================================================================================================

    useEffect(() => {
        const data = api.getCard.data;
        if (boardInit && data) {
            // project
            const currentProject = boardInit.projectContractVersions.find((item) => item.id === data.project.id);

            // contract
            const contractOptions = currentProject?.contracts;
            const currentContract = contractOptions?.find((item) => item.id === data.contract.id);

            // versionName
            const versionOptions = currentContract?.metaDataResponse;
            const currentVersion =
                versionOptions?.find((item) => item.id === data.metaData.id) || currentVersionFromStore;

            // WBS List Options
            if (currentVersion?.id) {
                mutateWBSOptions(
                    { versionId: currentVersion.id },
                    {
                        onSuccess: (res) => {
                            const wbsListOptions = sortAlphabeticalArray(res, 'name', 'asc').map(wbsMapper);
                            const currentWbsValue = wbsListOptions?.find(
                                (item) => item.id === data.relatedActivity.wbsId,
                            );
                            setValue('wbsValue', currentWbsValue || null);
                            setValue('wbsListOptions', wbsListOptions);
                        },
                    },
                );
            }

            if (data.relatedActivity.wbsId) {
                mutateActivityOptions(
                    { wbsId: data.relatedActivity.wbsId },
                    {
                        onSuccess: (res) => {
                            const activityListOptions = sortAlphabeticalArray(res, 'name', 'asc').map(wbsMapper);
                            const currentActivityId = activityListOptions?.find(
                                (item) => item.value === data.relatedActivity.activityId,
                            );
                            setValue('activityId', currentActivityId || null);
                            setValue('activityListOptions', activityListOptions);
                        },
                    },
                );
            }

            reset({
                id: data.id,
                project: currentProject ? mapper(currentProject) : null,
                contract: currentContract ? mapper(currentContract) : null,
                versionName: currentVersion ? versionMapper(currentVersion) : null,
                wbsValue: null,
                activityId: null,
                title: data.title,
                status: data.status,
                details: data.details,
                mentionedList: data.mentionedList,
                tagList: data.tagList.map((item) => ({ label: item.name, value: item.name })),
                assignee: assigneeMapper(data.assignee),
                category: mapper(data.category),
                isImportant: data.isImportant,
                startDate: data.startDate,
                endDate: data.endDate,
                externalLinks: data.externalLinks,
                versionId: currentVersion?.id,

                relatedCardIds: null,
                type: 'USER_GENERATED',

                // dates by activity id
                cardDates: {
                    baselinePlannedStartDate: moment(data.baselinePlannedStartDate).isValid()
                        ? moment(data.baselinePlannedStartDate).format(constants.formats.date.default)
                        : undefined,
                    baselinePlannedEndDate: moment(data.baselinePlannedEndDate).isValid()
                        ? moment(data.baselinePlannedEndDate).format(constants.formats.date.default)
                        : undefined,
                    plannedStartDate: moment(data.relatedActivity?.plannedStartDate).isValid()
                        ? moment(data.relatedActivity?.plannedStartDate).format(constants.formats.date.default)
                        : undefined,
                    plannedFinishDate: moment(data.relatedActivity?.plannedFinishDate).isValid()
                        ? moment(data.relatedActivity?.plannedFinishDate).format(constants.formats.date.default)
                        : undefined,
                    calculatedStartDate: moment(data.relatedActivity?.calculatedStartDate).isValid()
                        ? moment(data.relatedActivity?.calculatedStartDate).format(constants.formats.date.default)
                        : undefined,
                    calculatedFinishDate: moment(data.relatedActivity?.calculatedFinishDate).isValid()
                        ? moment(data.relatedActivity?.calculatedFinishDate).format(constants.formats.date.default)
                        : undefined,
                },

                // fieldsOptions
                projectOptions: boardInit.projectContractVersions.map(mapper),
                contractOptions: contractOptions ? contractOptions.map(mapper) : [],
                versionsOptions: versionOptions ? versionOptions.map(versionMapper) : [],
                wbsListOptions: [],
                activityListOptions: [],
            });
        }
    }, [boardInit, api.getCard.data]);

    const handleCommentData = () => {
        if (!isStringHasRealContent(commentData.comment)) {
            createEditCommentMutate(commentData, {
                onSuccess: () => {
                    return Promise.all([queryClient.invalidateQueries('getCard')]).then(() => {
                        resetCommentsEditorStatus();
                        setCommentData({
                            ...commentData,
                            cardId: api.getCard.data?.id,
                            isVirtual: api.getCard.data?.isVirtual,
                            comment: '',
                        });
                        setCommentId(false);
                    });
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            });
        }
    };

    const handleSave = (data) => {
        const payload = buildCreateCardPayload(data);

        mutateCreateCard(payload, {
            onSuccess: (res) => {
                handleSnackBar('Card Successfully Updated', 'success');
                api.getCard.refetch();
                saveCardCallback(res.id);
                reset(getValues());

                handleCommentData();
            },
            onError: (error) => {
                DEFAULT_ERROR({ error, handleError: handleSnackBar });
            },
            onSettled: () => {
                if (showConfirmModal) {
                    handleModalClose();
                }
            },
        });
    };

    const isStateEmpty = isEmpty(getValues());
    const isCardDirty = !isEmpty(dirtyFields) || !isStringHasRealContent(commentData.comment);

    const handleCardClose = () => {
        if (isCardDirty) {
            setShowConfirmModal(true);
        } else {
            handleModalClose();
        }
    };

    const handleConfirmModalSave = () => {
        handleSubmit(handleSave)();
    };

    //==================================== handleModalClose ============================================================
    const handleModalClose = () => {
        resetCommentsEditorStatus();
        resetCommentData();

        const pathWithoutParams = pathname.slice(0, pathname.lastIndexOf('/'));
        setShowConfirmModal(false);
        setShowModal(false);
        history.push({
            pathname: pathWithoutParams,
            search: search,
        });
    };
    //==================================================================================================================

    return (
        <Modal
            open={showModal}
            onClose={handleModalClose}
            width={'140rem'}
            title={'User Action'}
            actions={
                <>
                    <Button
                        size={'large'}
                        variant={'secondary'}
                        onClick={handleCardClose}
                        id={'cardModalCloseOrCancelButton'}
                    >
                        Close
                    </Button>
                    <Button
                        type={'submit'}
                        size={'large'}
                        variant={'primary'}
                        id={'cardModalSaveButton'}
                        disabled={!isCardDirty}
                        form={'user-action-form'}
                    >
                        Save
                    </Button>
                </>
            }
        >
            <form
                id={'user-action-form'}
                onSubmit={handleSubmit(handleSave)}
                className={classes.userGeneratedCardContainer}
            >
                {isLoading && <OverlayWithSpinner />}

                {!isStateEmpty && (
                    <>
                        <SideMenu
                            title={api.getCard.data?.title}
                            taskId={api.getCard.data?.taskId}
                            id={api.getCard.data?.id}
                            handleModalClose={handleModalClose}
                            deleteCardCallback={deleteCardCallback}
                        />

                        <CardHeader control={control} setValue={setValue} watch={watch} errors={errors} />
                        <div className={classes.tabsWithStatusWrapper}>
                            <TabsSection tabs={tabsArrData} activeTab={activeTab} setActiveTab={setActiveTab} />
                            <UserGeneratedCardStatusSelect control={control} />
                        </div>

                        <CardContent
                            activeTab={activeTab}
                            boardInit={boardInit}
                            setValue={setValue}
                            watch={watch}
                            control={control}
                            errors={errors}
                            data={api.getCard.data}
                            queryKeys={queryKeys}
                        />
                    </>
                )}
            </form>

            <ConfirmChangesModal
                showModal={showConfirmModal}
                onHideModal={() => setShowConfirmModal(false)}
                handleCloseWithoutSave={handleModalClose}
                handleSave={handleConfirmModalSave}
                isLoading={createCardLoading}
            >
                You made changes to this card. Would you like to save it?
            </ConfirmChangesModal>
        </Modal>
    );
};
